import { i18n } from '../../i18n/i18n'

import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { produce } from 'immer'
import { RootState } from '../../store'
import {
  replaceStageUnit,
  replaceStageWidth,
  replaceStageHeight,
  replaceGridVisibility,
  replaceGridSize,
  replaceStageDpi,
  resetStageSize,
  StageUnit,
  GridVisibility
} from '../../views/LabelBuilder/LabelBuilder.slice'
import Card from '../Card'
import Heading from '../Heading'
import HeadingRow from '../HeadingRow'
import InputText from '../InputText'
import Label from '../Label'
import Row from '../Row/Row'
import Select, { SelectOption } from '../Select'

interface Props {
}

type CombinedProps = Props & ConnectedProps<typeof connector>

class TemplateProperties extends Component<CombinedProps> {
  gridVisibilityOptions: SelectOption[] = [
    { name: 'On', value: 'on' },
    { name: 'Off', value: 'off' }
  ]

  sizeUnitOptions: SelectOption[] = [
    { name: 'Inch', value: 'in' },
    { name: 'Millimeter', value: 'mm' },
    { name: 'Pixel', value: 'px' }
  ]

  dpiOptions: SelectOption[] = [
    { name: '203 dpi', value: '203' },
    { name: '300 dpi', value: '300' },
    { name: '600 dpi', value: '600' }
  ]

  get active () {
    const { active } = this.props
    if (active === null) return
    const type = active ? 'success' : 'warning'
    const text = active ? 'Active' : 'Draft'
    return <Label type={type} text={text} size="regular"/>
  }

  handleInputChange = (name: string, value: string) => {
    if (name === 'unit') {
      const unit = value as StageUnit
      this.props.replaceStageUnit(unit)
      this.props.resetStageSize(unit)
    } else if (name === 'width') {
      this.props.replaceStageWidth(Number(value))
    } else if (name === 'height') {
      this.props.replaceStageHeight(Number(value))
    } else if (name === 'gridVisibility') {
      this.props.replaceGridVisibility(value as GridVisibility)
    } else if (name === 'gridSize') {
      this.props.replaceGridSize(Number(value))
    } else if (name === 'dpi') {
      this.props.replaceStageDpi(Number(value))
    }
  }

  render () {
    localStorage.setItem('canvasUnit', this.props.unit)
    localStorage.setItem('canvasWidth', this.props.width)
    localStorage.setItem('canvasHeight', this.props.height)

    return (
            <Card flat={true} className="pb-2">
                <HeadingRow heading={i18n('Label Designer')}>
                    {this.active}
                </HeadingRow>
                <div className='-mt-6 flex gap-x-5 flex-col xl:flex-row'>
                    <div>
                        <Heading
                            className="mb-1"
                            text={i18n('Label')}
                            size="small"/>
                        <Row column={4}>
                            <Select
                                name="unit"
                                value={this.props.unit}
                                options={this.sizeUnitOptions}
                                label={i18n('Unit')}
                                onChange={this.handleInputChange}/>
                            <InputText
                                type="number"
                                name="width"
                                value={this.props.width}
                                label={i18n('Width')}
                                onChange={this.handleInputChange}/>
                            <InputText
                                type="number"
                                name="height"
                                value={this.props.height}
                                label={i18n('Height')}
                                onChange={this.handleInputChange}/>
                            <Select
                                name="dpi"
                                value={this.props.dpi}
                                options={this.dpiOptions}
                                label={i18n('Printer resolution')}
                                onChange={this.handleInputChange}/>
                        </Row>
                    </div>
                    <div>
                        <Heading
                            className="mb-1"
                            text={i18n('Grid')}
                            size="small"/>
                        <Row column={2}>
                            <Select
                                name="gridVisibility"
                                value={this.props.grid.visibility}
                                options={this.gridVisibilityOptions}
                                label={i18n('Visibility')}
                                onChange={this.handleInputChange}/>
                            <InputText
                                type="number"
                                name="gridSize"
                                value={String(this.props.grid.size)}
                                label={i18n('Size')}
                                onChange={this.handleInputChange}/>
                        </Row>
                    </div>
                </div>
            </Card>
    )
  }
}

function mapStateTopProps (state: RootState, props: Props) {
  const { labelBuilder } = produce(state, draft => draft)
  const {
    width,
    height,
    unit,
    grid,
    dpi
  } = labelBuilder.markup.stage
  const { active } = labelBuilder
  return {
    active,
    width: String(width),
    height: String(height),
    unit,
    grid,
    // Labels saved before this setting existed are 203 dpi.
    dpi: String(dpi || 203)
  }
}

const mapDispatchToProps = {
  replaceStageUnit,
  replaceStageWidth,
  replaceStageHeight,
  resetStageSize,
  replaceGridVisibility,
  replaceGridSize,
  replaceStageDpi
}

const connector = connect(mapStateTopProps, mapDispatchToProps)
export default connector(TemplateProperties)
